/** @jsx h */
import { Provider } from '@uppy/companion-client';
import classNames from 'classnames';
import { UIPlugin } from '@uppy/core';
import { ProviderViews } from '@uppy/provider-views';
import { h } from 'preact';
import Logger from '../../Logger';

import { getGSuiteMimeType, getItemName } from './helpers';
import locale from './locale';

import './styles.scss';

export default class GooglePicker extends UIPlugin {
  constructor(uppy, opts) {
    super(uppy, opts);
    this.type = 'acquirer';
    this.files = [];
    this.id = this.opts.id || 'GooglePicker';
    Provider.initPlugin(this, opts);
    this.icon = () => (
      <svg
        aria-hidden="true"
        focusable="false"
        width="32"
        height="32"
        viewBox="0 0 32 32"
      >
        <g fill="none" fillRule="evenodd">
          <rect
            className="uppy-ProviderIconBg"
            fill="#4285F4"
            width="32"
            height="32"
            rx="16"
          />
          <path
            d="M25.216 17.736L19.043 7h-6.086l6.175 10.736h6.084zm-11.275.896L10.9 24h11.723l3.04-5.368H13.942zm-1.789-10.29l-5.816 10.29L9.38 24l5.905-10.29-3.132-5.369z"
            fill="#FFF"
          />
        </g>
      </svg>
    );

    this.logger = new Logger({
      datadogClientToken: this.opts.loggerConfig.datadogClientToken,
      context: this.opts.loggerConfig.loggerContext,
      enableConsole: this.opts.loggerConfig.debug,
    });

    this.picker = null;

    this.enableMultipleSelect = this.opts.multiple;
    this.pickerFileTypes = this.opts.fileTypesConfig.permittedMimeTypesForGooglePicker?.join(',');

    this.provider = new Provider(uppy, {
      companionUrl: this.opts.companionUrl,
      companionHeaders: this.opts.companionHeaders,
      companionKeysParams: this.opts.companionKeysParams,
      companionCookiesRule: this.opts.companionCookiesRule,
      provider: 'google_picker_provider',
      pluginId: this.id,
      supportsRefreshToken: true,
    });

    this.defaultLocale = locale;

    this.i18nInit();
    this.title = this.i18n('pluginNameGooglePicker');

    this.onFirstRender = this.onFirstRender.bind(this);
    this.onApiLoad = this.onApiLoad.bind(this);
    this.onPickerApiLoad = this.onPickerApiLoad.bind(this);
    this.gsiLoaded = this.gsiLoaded.bind(this);
    this.injectScripts = this.injectScripts.bind(this);
    this.logout = this.logout.bind(this);
    this.pickerCallback = this.pickerCallback.bind(this);
    this.createPicker = this.createPicker.bind(this);
    this.showPicker = this.showPicker.bind(this);
    this.onMount = this.onMount.bind(this);
    this.fetchGoogleOAuthToken = this.fetchGoogleOAuthToken.bind(this);
    this.buildAndShowPicker = this.buildAndShowPicker.bind(this);
    this.checkAuthToken = this.checkAuthToken.bind(this);
    this.handleError = this.handleError.bind(this);
    this.render = this.render.bind(this);

    this.opts = { ...opts };
  }

  // These values represent the default values for the custom state of this
  // component. Note that this would not actually set the default set if it were
  // uncommented
  // state = {
  //   gapiInited: false,
  //   gsiInited: false,
  //   pickerInited: false,
  //   pickerCreated: false,
  //   pickerVisible: false,
  //   buildingAndShowingPicker: false,
  //   googleOAuthToken: null,
  //   googlePickerDeveloperKey: null,
  //   googlePickerAppId: null,
  //   loggingOut: false,
  // };

  install() {
    this.view = new ProviderViews(this, {
      provider: this.provider,
      showFilter: false,
    });

    const { target } = this.opts;
    if (target) {
      this.mount(target, this);
    }
  }

  uninstall() {
    this.view.tearDown();
    this.unmount();
  }

  onMount() {
    if (window.gapi) {
      this.setPluginState({ gapiInited: true });
    }
    if (window.google?.accounts) {
      this.setPluginState({ gsiInited: true });
    }
    this.injectScripts()
      .then(() => {
        const overlay = document.createElement('div');
        overlay.id = 'google-picker-ui-overlay';
        document.body.appendChild(overlay);
      })
      .then(() => {
        this.fetchGoogleOAuthToken();
      });
  }

  fetchGoogleOAuthToken() {
    const encryptedJWT = localStorage.getItem('companion-GooglePicker-auth-token');
    if (
      (this.getPluginState().googleOAuthToken && this.getPluginState().authenticated)
        || this.getPluginState().buildingAndShowingPicker
        || !encryptedJWT
    ) return Promise.resolve();
    this.setPluginState({ buildingAndShowingPicker: true });
    return fetch(`${this.provider.opts.companionUrl}/fetch-google-credentials?encryptedToken=${encryptedJWT}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch Google OAuth token');
        }
        return response.json();
      })
      .then((data) => {
        this.setPluginState({
          googleOAuthToken: data.google_picker_provider.accessToken,
          googlePickerDeveloperKey: data.google_picker_provider.developerKey,
          googlePickerAppId: data.google_picker_provider.appId,
          buildingAndShowingPicker: false
        });
      });
  }

  buildAndShowPicker() {
    this.fetchGoogleOAuthToken()
      .then(() => this.createPicker())
      .then(() => this.showPicker())
      .catch((error) => {
        this.logger.error('GooglePicker buildAndShow error', error);
      });
  }

  onUnmount() {
    document.getElementById('google-picker-ui-overlay').style.display = 'none';
    this.setPluginState({ pickerVisible: false });
  }

  injectScripts() {
    return new Promise((resolve) => {
      const promises = [];

      if (!this.getPluginState().gapiInited) {
        promises.push(new Promise((res) => {
          const gapiScript = document.createElement('script');
          gapiScript.src = 'https://apis.google.com/js/api.js';
          gapiScript.onload = () => {
            this.onApiLoad(res);
          };
          gapiScript.async = true;
          document.body.append(gapiScript);
        }));
      }

      if (!this.getPluginState().gsiInited) {
        promises.push(new Promise((res) => {
          const gsiClientScript = document.createElement('script');
          gsiClientScript.src = 'https://accounts.google.com/gsi/client';
          gsiClientScript.onload = () => {
            this.gsiLoaded();
            res();
          };
          gsiClientScript.async = true;
          document.body.append(gsiClientScript);
        }));
      }

      Promise.all(promises).then(resolve);
    });
  }

  onApiLoad(resCallback) {
    window.gapi.load('client:picker', () => {
      this.onPickerApiLoad(resCallback);
    });
  }

  onPickerApiLoad(resCallback) {
    window.gapi.client.load('https://www.googleapis.com/discovery/v1/apis/drive/v3/rest')
      .then(() => {
        this.setPluginState({ pickerInited: true });
        resCallback();
      });
  }

  gsiLoaded() {
    this.setPluginState({ gsiInited: true });
  }

  async pickerCallback(data) {
    if (data.action === window.google.picker.Action.PICKED) {
      const sanitizedDocuments = data.docs.map((document) => ({
        ...document,
        requestPath: document.id,
        mimeType: getGSuiteMimeType(document.mimeType),
        name: getItemName(document),
      }));

      this.setPluginState({ currentSelection: sanitizedDocuments });

      try {
        this.view.donePicking();
      } catch (e) {
        this.logger.error('GooglePicker file selection error', e);
      } finally {
        this.uppy.getPlugin('react:Dashboard').setPluginState({ activePickerPanel: false });
      }
    }
    if (data.action === window.google.picker.Action.CANCEL) {
      this.picker.setVisible(false);
      document.getElementById('google-picker-ui-overlay').style.display = 'none';
    }
  }

  createPicker() {
    const {
      pickerCreated,
      authenticated,
      googleOAuthToken,
      googlePickerDeveloperKey,
      googlePickerAppId,
    } = this.getPluginState();
    if (pickerCreated || !authenticated || !googleOAuthToken) return Promise.resolve();

    return new Promise((resolve, reject) => {
      try {
        const view = new window.google.picker.DocsView()
          .setIncludeFolders(true)
          .setMimeTypes(this.pickerFileTypes)
          .setMode(window.google.picker.DocsViewMode.LIST);

        const enableMultipleSelect = this.enableMultipleSelect
          ? window.google.picker.Feature.MULTISELECT_ENABLED
          : window.google.picker.Feature.MULTISELECT_DISABLED;

        this.picker = new window.google.picker.PickerBuilder()
          .addView(view)
          .setOAuthToken(googleOAuthToken)
          .setDeveloperKey(googlePickerDeveloperKey)
          .setCallback(this.pickerCallback)
          .enableFeature(enableMultipleSelect)
          .setAppId(googlePickerAppId)
          .build();

        this.setPluginState({ pickerCreated: true });

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  showPicker() {
    if (
      !this.getPluginState().authenticated
      || !this.getPluginState().pickerCreated
      || this.getPluginState().loggingOut
      || this.picker.isVisible()
    ) return;

    document.getElementById('google-picker-ui-overlay').style.display = 'block';
    this.picker.setVisible(true);
    this.setPluginState({ pickerVisible: true });
  }

  logout() {
    // The logout function native to the Uppy provider-views package triggers
    // a re-render before any of its internal logic gets run and I cannot for
    // the life of me figure out why. We need some state here to prevent
    // the picker window from opening again before the logout is complete
    // and we can set authenticated to false again
    this.setPluginState({ loggingOut: true });
    this.provider.logout()
      .then((res) => {
        if (res.ok) {
          const newState = {
            authenticated: false,
            loggingOut: false,
            pickerCreated: false,
            googleOAuthToken: null,
            files: [],
            folders: [],
            directories: [],
          };
          this.setPluginState(newState);
          this.picker.dispose();
        }
      }).catch(this.handleError);
  }

  handleError(error) {
    const message = this.uppy.i18n('companionError');

    this.uppy.log(error.toString());

    if (error.isAuthError) {
      this.setPluginState({ googleOAuthToken: null });
      return;
    }

    this.uppy.info({ message, details: error.toString() }, 'error', 5000);
  }

  checkAuthToken() {
    return this.view.sharedHandler.loaderWrapper(
      this.provider.list(),
      () => {},
      this.handleError,
    );
  }

  // On first render, we need to check if the current auth token is still valid
  // Per Uppy's pattern for the other providers, we make a GET request to the list
  // endpoint and null out the token if it has expired
  onFirstRender() {
    Promise.all([
      this.checkAuthToken(),
    ]);
  }

  render(state) {
    // Uppy provider-views calls this method again when you hit cancel to go back
    // if this is the case, we don't want to launch the picker again
    // Likewise, if we've already selected images, we want to show the image resizer
    const dashboardPlugin = this.uppy.getPlugin('react:Dashboard');
    const loadingStatusPlugin = this.uppy.getPlugin('LoadingStatus');
    const { isLoading: isLoadingRemoteImageDownload } = loadingStatusPlugin.getPluginState();

    if (!dashboardPlugin.getPluginState().activePickerPanel || isLoadingRemoteImageDownload) {
      document.getElementById('google-picker-ui-overlay').style.display = 'none';
      return null;
    }

    const isAuthenticated = this.getPluginState().authenticated;

    if (isAuthenticated) {
      this.buildAndShowPicker();

      return (
        <div className="uppy-ProviderBrowser uppy-ProviderBrowser-viewType--list">
          <div className="uppy-ProviderBrowser-header">
            <div
              className={classNames(
                'uppy-ProviderBrowser-headerBar',
                'uppy-ProviderBrowser-headerBar--simple',
              )}
            >
              <div className="uppy-Provider-breadcrumbs">
                <div className="uppy-Provider-breadcrumbsIcon">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                  >
                    <g fill="none" fillRule="evenodd">
                      <rect
                        className="uppy-ProviderIconBg"
                        fill="#4285F4"
                        width="32"
                        height="32"
                        rx="16"
                      />
                      <path
                        d="M25.216 17.736L19.043 7h-6.086l6.175 10.736h6.084zm-11.275.896L10.9 24h11.723l3.04-5.368H13.942zm-1.789-10.29l-5.816 10.29L9.38 24l5.905-10.29-3.132-5.369z"
                        fill="#FFF"
                      />
                    </g>
                  </svg>
                </div>
                <button type="button" className="uppy-u-reset uppy-c-btn">Google Drive</button>
              </div>
              <button type="button" onClick={this.logout} className="uppy-u-reset uppy-ProviderBrowser-userLogout" key="logout">
                Log out
              </button>
            </div>
          </div>
          <div className="uppy-ProviderBrowser-body" style={{ display: 'flex' }}>
            <div className="uppy-Provider-auth">
              <div className="uppy-Provider-authIcon" style={{ marginBottom: '15px' }}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                >
                  <g fill="none" fillRule="evenodd">
                    <rect
                      className="uppy-ProviderIconBg"
                      fill="#4285F4"
                      width="32"
                      height="32"
                      rx="16"
                    />
                    <path
                      d="M25.216 17.736L19.043 7h-6.086l6.175 10.736h6.084zm-11.275.896L10.9 24h11.723l3.04-5.368H13.942zm-1.789-10.29l-5.816 10.29L9.38 24l5.905-10.29-3.132-5.369z"
                      fill="#FFF"
                    />
                  </g>
                </svg>
              </div>
              <div className="uppy-Provider-authTitle">
                Upload your files from Google Drive
              </div>
              <div className="uppy-Provider-authTitle">
                <button type="button" className="uppy-u-reset uppy-c-btn uppy-c-btn-primary uppy-Provider-authBtn uppy-Provider-btn-google" onClick={this.showPicker}>
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 26 26"
                  >
                    <g fill="none" fillRule="evenodd">
                      <circle fill="#FFF" cx="13" cy="13" r="13" />
                      <path d="M21.64 13.205c0-.639-.057-1.252-.164-1.841H13v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" fillRule="nonzero" />
                      <path d="M13 22c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H4.957v2.332A8.997 8.997 0 0013 22z" fill="#34A853" fillRule="nonzero" />
                      <path d="M7.964 14.71A5.41 5.41 0 017.682 13c0-.593.102-1.17.282-1.71V8.958H4.957A8.996 8.996 0 004 13c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" fillRule="nonzero" />
                      <path d="M13 7.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C17.463 4.891 15.426 4 13 4a8.997 8.997 0 00-8.043 4.958l3.007 2.332C8.672 9.163 10.656 7.58 13 7.58z" fill="#EA4335" fillRule="nonzero" />
                      <path d="M4 4h18v18H4z" />
                    </g>
                  </svg>
                  Select files from Google Drive
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.view.render(state);
  }
}
